import { watch } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { $axios, $queryClient } from '@/boot/initApi'

// setTimeout overflows (fires immediately) past 2^31-1 ms (~24.8 days).
const MAX_TIMEOUT_MS = 2 ** 31 - 1

const queryKey = ['maintenance']

const queryFn = async () => {
  // Short timeout: the boot fetch is awaited before first paint.
  const { data } = await $axios.get('api/maintenance', { timeout: 5000 })
  return data.data
}

export default ({ maintenance, now }) => {
  let startTimer = null

  const armStartTimer = () => {
    clearTimeout(startTimer)
    startTimer = null

    const { active, startsAt } = maintenance.value
    if (active || !startsAt) return

    const delay = Date.parse(startsAt) - Date.now()
    if (delay <= 0) {
      now.value = Date.now()
      return
    }

    startTimer = setTimeout(() => {
      now.value = Date.now()
      // Re-arm when the delay was clamped, until startsAt is actually reached.
      armStartTimer()
    }, Math.min(delay, MAX_TIMEOUT_MS))
  }

  /**
   * Single entry point for every maintenance signal: boot fetch, polling,
   * 503 responses and the `maintenance` broadcast channel.
   */
  const setMaintenance = payload => {
    if (!payload) return
    maintenance.value = {
      active: !!payload.active,
      message: payload.message ?? null,
      startsAt: payload.startsAt ?? null,
      endsAt: payload.endsAt ?? null
    }
    // Keep the query cache in step with pushed signals (503, Echo) — otherwise
    // the screen's useQuery mounts on a stale boot payload and immediately
    // re-applies it, flickering the screen off and on.
    $queryClient.setQueryData(queryKey, maintenance.value)
    now.value = Date.now()
    armStartTimer()
  }

  return {
    setMaintenance,

    fetchMaintenance: async () => {
      try {
        setMaintenance(await $queryClient.fetchQuery({ queryKey, queryFn, staleTime: 0 }))
      } catch (err) {
        // The app must still boot when the status endpoint is unreachable.
        console.error(err)
      }
    },

    showMaintenance: ({ refetchInterval = false } = {}) => {
      const query = useQuery({ queryKey, queryFn, refetchInterval, staleTime: 0 }, $queryClient)

      watch(query.data, val => {
        if (val) setMaintenance(val)
      }, { immediate: true })

      return query
    }
  }
}
